import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  AreaChart
} from "recharts";

import "../styles/allgraph.css";
import { useState, useRef, useEffect, useMemo, useCallback, memo } from "react";

// ─── Constants ────────────────────────────────────────────────────────────────
const YAXIS_WIDTH        = 40;
const CHART_RIGHT_MARGIN = 10;
const TOOLTIP_MARGIN     = 10; // px kept clear from the container's edges
const STALE_COLOR        = "#94a3b8"; // grey used for "(last value)" fallback

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

// ─── Value lookup helpers ─────────────────────────────────────────────────────

// Forward-fills: walks up to hoverTime and returns the last non-null value
// seen, no matter how far back it was. Useful for cumulative-style rows
// (Steps) and as the SOURCE of the grey "(last value)" fallback — but should
// never be used directly to decide whether a line currently has data.
function getStepValue(data, hoverTime) {
  if (!data || hoverTime == null) return null;
  let lastValid = null;
  for (let i = 0; i < data.length; i++) {
    if (data[i].time > hoverTime) break;
    if (data[i].value != null) lastValid = data[i].value;
  }
  return lastValid;
}

// Timestamp of the LAST non-null reading in the whole array — i.e. the
// point where this row's line actually stops for the day. Used only to
// detect "there is no more data after this time", not for gaps in between.
function getLastDataTime(data) {
  if (!data?.length) return null;
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i].value != null) return data[i].time;
  }
  return null;
}

function getInterpolatedValue(data, hoverTime) {
  if (!data || hoverTime == null) return null;
  let left = null, right = null;
  for (let i = 0; i < data.length - 1; i++) {
    const curr = data[i], next = data[i + 1];
    if (curr.value == null) continue;
    if (curr.time <= hoverTime && next.time >= hoverTime) {
      left  = curr;
      right = next.value != null ? next : curr;
      break;
    }
  }
  if (!left) return null;
  if (!right || left.time === right.time) return left.value;
  const ratio = (hoverTime - left.time) / (right.time - left.time);
  return Math.round(left.value + ratio * (right.value - left.value));
}

function getCumulative(data, hoverTime) {
  if (!data || hoverTime == null) return 0;
  let total = 0;
  for (let i = 0; i < data.length; i++) {
    if (data[i].time <= hoverTime && data[i].value != null) total += data[i].value;
  }
  return total;
}

function formatTime(ts) {
  if (!ts) return "";
  return new Date(ts).toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", hour12: true
  });
}

// ─── Stage config ─────────────────────────────────────────────────────────────
const STAGE_CFG = {
  awake:       { label: "Awake", color: "#FFB74D" },
  rem_sleep:   { label: "REM",   color: "#C084FC" },
  light_sleep: { label: "Light", color: "#A78BFA" },
  deep_sleep:  { label: "Deep",  color: "#6366F1" },
};

const STAGE_Y = { awake: 0, rem_sleep: 25, light_sleep: 50, deep_sleep: 75 };

// ─── Combined-tooltip display config ──────────────────────────────────────────
// Maps a row's `key` (as produced in AllGraphs.jsx) to the label / unit shown
// inside the single shared tooltip box.
const METRIC_DISPLAY = {
  HR:     { label: "Heart Rate", unit: "bpm"   },
  SPO2:   { label: "Spo2",       unit: "%"     },
  HRV:    { label: "HRV",        unit: "ms"    },
  CGM:    { label: "CGM",        unit: "mg/dL" },
  Stress: { label: "Stress",     unit: ""      },
  Steps:  { label: "Steps",      unit: ""      },
};

// ─── VitalFilterDropdown ──────────────────────────────────────────────────────
const VitalFilterDropdown = memo(function VitalFilterDropdown({ rows, visibleKeys, onToggle }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const visibleCount = rows.filter(r => visibleKeys.has(r.key)).length;

  return (
    <div className="vital-filter-wrapper" ref={ref} style={{ position: "relative" }}>
      <button
        className="vital-filter-btn"
        onClick={() => setOpen(o => !o)}
        title="Show / hide vitals"
      >
        Vitals
        <span style={{
          marginLeft: 6, fontSize: 10, background: "#6366f1",
          color: "#fff", borderRadius: 10, padding: "1px 6px",
        }}>
          {visibleCount}/{rows.length}
        </span>
        <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.6 }}>▾</span>
      </button>

      {open && (
        <div className="vital-filter-dropdown">
          {rows.map(r => {
            const checked = visibleKeys.has(r.key);
            return (
              <label key={r.key} className="vital-filter-item" style={{ "--dot-color": r.color }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => onToggle(r.key)}
                />
                <span className="vital-filter-dot" />
                <span className="vital-filter-label">{r.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
});

// ─── SleepStageRow ────────────────────────────────────────────────────────────
// Per-row tooltip / cursor-line removed — the single CombinedTooltip (rendered
// by Graphcontainer) now shows the current stage alongside every other vital.
const SleepStageRow = memo(function SleepStageRow({ segments, viewStart, viewEnd }) {
  const domainRange = (viewEnd - viewStart) || 1;
  const hasData = segments?.length > 0;

  return (
    <div className="allgraph-row">
      <div className="allgraph-icon">
        <span style={{ fontSize: 20 }}>🌙</span>
        <span className="icon-label">Sleep</span>
      </div>

      <div className="allgraph-chart" style={{ position: "relative" }}>
        <div style={{
          position: "absolute",
          left: YAXIS_WIDTH, right: CHART_RIGHT_MARGIN,
          top: 10, bottom: 10,
          borderRadius: 6, overflow: "hidden", background: "#f8fafc",
        }}>
          {hasData ? (
            <>
              {segments.map((seg, i) => {
                const startPct = ((seg.startMs - viewStart) / domainRange) * 100;
                const widthPct = ((seg.endMs - seg.startMs) / domainRange) * 100;
                const color    = STAGE_CFG[seg.stage]?.color || "#A78BFA";
                const stageY   = STAGE_Y[seg.stage] ?? 50;
                return (
                  <div key={i}
                    title={`${STAGE_CFG[seg.stage]?.label || seg.stage}: ${formatTime(seg.startMs)} → ${formatTime(seg.endMs)}`}
                    style={{
                      position: "absolute",
                      left: `${Math.max(0, startPct)}%`, width: `${Math.max(0.2, widthPct)}%`,
                      top: `${stageY}%`, height: "25%",
                      background: color, opacity: 0.85,
                    }}
                  />
                );
              })}

              <div style={{
                position: "absolute", left: -YAXIS_WIDTH, width: YAXIS_WIDTH - 4,
                top: 0, bottom: 0, display: "flex", flexDirection: "column",
                justifyContent: "space-around", pointerEvents: "none",
              }}>
                {Object.entries(STAGE_CFG).map(([stage, cfg]) => (
                  <span key={stage} style={{
                    fontSize: 8, color: cfg.color, fontWeight: 600,
                    textAlign: "right", paddingRight: 4, lineHeight: 1,
                  }}>{cfg.label}</span>
                ))}
              </div>

              {[0, 25, 50, 75, 100].map(p => (
                <div key={p} style={{
                  position: "absolute", left: 0, right: 0, top: `${p}%`,
                  height: "1px", background: "#e2e8f0",
                }} />
              ))}
            </>
          ) : (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              height: "100%", fontSize: 11, color: "#9ca3af",
            }}>No sleep data</div>
          )}
        </div>

        <div style={{ position: "absolute", left: 0, width: YAXIS_WIDTH, top: 0, bottom: 0, background: "white" }} />
      </div>
    </div>
  );
});

// ─── GradientDef ─────────────────────────────────────────────────────────────
const GradientDef = memo(function GradientDef({ id, color }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="5%"   stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0}   />
      </linearGradient>
    </defs>
  );
});

// ─── TimelineRow ──────────────────────────────────────────────────────────────
// Per-row tooltip removed — values live in the single CombinedTooltip instead.
// What each row DOES still do on its own: highlight the exact point that sits
// under the shared cursor line, so every graph's point at that moment lights
// up together (not just whichever one the mouse happens to be over).
const TimelineRow = memo(function TimelineRow({
  icon, label, data, color, viewStart, viewEnd, overlay, overlayRef, hoverTime
}) {
  const ownRef   = useRef(null);
  const chartRef = overlayRef || ownRef;

  const isSteps  = label === "Steps";
  const showDots = ["HR", "SPO2", "HRV", "Stress"].includes(label);

  const gradientId = `gradient-${label}`;
  const domain = useMemo(() => [viewStart, viewEnd], [viewStart, viewEnd]);

  // Find the data index nearest the shared hover time — buckets are evenly
  // spaced, so this can be computed directly instead of scanning.
  const highlightIndex = useMemo(() => {
    if (hoverTime == null || !data?.length) return -1;
    if (data.length === 1) return 0;
    const step = data[1].time - data[0].time;
    if (!step) return -1;
    const idx = Math.round((hoverTime - data[0].time) / step);
    return clamp(idx, 0, data.length - 1);
  }, [hoverTime, data]);

  return (
    <div className="allgraph-row">
      <div className="allgraph-icon">
        <img src={icon} alt="" />
        <span className="icon-label">{label}</span>
      </div>

      <div className="allgraph-chart" ref={chartRef}>
        {/* Calorie overlay pins — only present on CGM row, null on all others */}
        {overlay}

        <ResponsiveContainer width="100%" height={110}>
          {isSteps ? (
            <BarChart data={data} margin={{ top: 10, bottom: 10, left: 0, right: CHART_RIGHT_MARGIN }}>
              <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#d1d5db" />
              <YAxis width={YAXIS_WIDTH} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <XAxis dataKey="time" type="number" domain={domain} hide />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={14} isAnimationActive={false}>
                {data.map((entry, idx) => (
                  <Cell
                    key={idx}
                    fill={color}
                    opacity={idx === highlightIndex ? 1 : 0.55}
                    stroke={idx === highlightIndex ? "#fff" : "none"}
                    strokeWidth={idx === highlightIndex ? 1.5 : 0}
                  />
                ))}
              </Bar>
            </BarChart>
          ) : (
            <AreaChart data={data} margin={{ top: 10, bottom: 10, left: 0, right: CHART_RIGHT_MARGIN }}>
              <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#d1d5db" />
              <YAxis width={YAXIS_WIDTH} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <XAxis dataKey="time" type="number" domain={domain} hide />
              <GradientDef id={gradientId} color={color} />
              <Area
                type="monotone" dataKey="value" stroke={color}
                fill={`url(#${gradientId})`}
                connectNulls={true} isAnimationActive={false} baseValue={0}
                dot={(dotProps) => {
                  const { cx, cy, payload, index } = dotProps;
                  if (payload.value == null) return null;
                  if (index === highlightIndex) {
                    return (
                      <circle
                        key="hl"
                        cx={cx} cy={cy} r={5}
                        fill={color} stroke="#fff" strokeWidth={2}
                      />
                    );
                  }
                  return showDots
                    ? <circle key={index} cx={cx} cy={cy} r={2.5} fill={color} />
                    : null;
                }}
              />
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
});

// ─── CombinedTooltip ──────────────────────────────────────────────────────────
// The single shared tooltip box. Positioned in *container* coordinates:
//  - left  tracks the hovered time (same x for every row, clamped to width)
//  - top   tracks the mouse's own y position inside the container, clamped
//          so the box never overflows the card's top/bottom edge.
const CombinedTooltip = memo(function CombinedTooltip({ hoverTime, metrics, left, top, boxRef }) {
  if (hoverTime == null || !metrics.length) return null;

  return (
    <div
      ref={boxRef}
      style={{
        position:     "absolute",
        left,
        top,
        zIndex:       30,
        background:   "#ffffff",
        border:       "2px solid #3b82f6",
        borderRadius: 14,
        padding:      "10px 14px",
        minWidth:     150,
        boxShadow:    "0 8px 20px rgba(0,0,0,0.15)",
        pointerEvents:"none",
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>
        {formatTime(hoverTime)}
      </div>
      {metrics.map(m => (
        <div
          key={m.key}
          style={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
            gap:            14,
            fontSize:       12,
            padding:        "2px 0",
          }}
        >
          <span style={{ display: "flex", alignItems: "center", gap: 6, color: "#334155" }}>
            <span style={{
              width: 8, height: 8, borderRadius: "50%",
              background: m.color, flexShrink: 0,
            }} />
            {m.label}
          </span>
          <span style={{ fontWeight: 600, color: m.isGrey ? STALE_COLOR : "#0f172a" }}>
            {m.display}
          </span>
        </div>
      ))}
    </div>
  );
});

// ─── Graphcontainer ───────────────────────────────────────────────────────────
export default function Graphcontainer({
  rows,
  cursorIndex,
  setCursorIndex,
  dateControl,
  selectedDate,
  tooltipsDisabled,
  sleepMode,
  sleepStageData,
}) {
  const containerRef = useRef(null);
  const tooltipRef   = useRef(null);

  const [hoverTime,    setHoverTime]    = useState(null);
  const [cursorPx,     setCursorPx]     = useState(0); // x position, relative to container
  const [mouseY,       setMouseY]       = useState(0); // y position, relative to container

  // ── Visible vitals filter ─────────────────────────────────────────────────
  const rowKeyString = rows.map(r => r.key).join(",");
  const [visibleKeys, setVisibleKeys] = useState(() => new Set(rows.map(r => r.key)));

  useEffect(() => {
    setVisibleKeys(new Set(rows.map(r => r.key)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleepMode, rowKeyString]);

  const toggleKey = useCallback((key) => {
    setVisibleKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  }, []);

  const visibleRows = useMemo(
    () => sleepMode ? rows.filter(r => visibleKeys.has(r.key)) : rows,
    [sleepMode, rows, visibleKeys]
  );

  // ── View domain ───────────────────────────────────────────────────────────
  const { viewStart, viewEnd, viewRange } = useMemo(() => {
    const dayStart = (() => { const d = new Date(selectedDate); d.setHours(0,0,0,0);      return d.getTime(); })();
    const dayEnd   = (() => { const d = new Date(selectedDate); d.setHours(23,59,59,999); return d.getTime(); })();
    const vs = (sleepMode && sleepStageData?.sleepStart) ? sleepStageData.sleepStart : dayStart;
    const ve = (sleepMode && sleepStageData?.sleepEnd)   ? sleepStageData.sleepEnd   : dayEnd;
    return { viewStart: vs, viewEnd: ve, viewRange: ve - vs };
  }, [selectedDate, sleepMode, sleepStageData]);

  // ── Mouse move — throttled to rAF ─────────────────────────────────────────
  const rafId = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (tooltipsDisabled) return;
    if (rafId.current) return;

    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      const containerEl = containerRef.current;
      const chartEl = containerEl?.querySelector(".allgraph-chart");
      if (!containerEl || !chartEl) return;

      const containerRect = containerEl.getBoundingClientRect();
      const rect = chartEl.getBoundingClientRect();
      const pw   = rect.width - YAXIS_WIDTH - CHART_RIGHT_MARGIN;
      const rawX = e.clientX - rect.left - YAXIS_WIDTH;
      const pct  = clamp(rawX, 0, pw) / pw;

      // x position translated into container-relative pixels (charts share
      // the same left offset across every row, so this is valid for all of them)
      const chartOffsetX = rect.left - containerRect.left;

      setHoverTime(viewStart + pct * viewRange);
      setCursorPx(chartOffsetX + YAXIS_WIDTH + pct * pw);
      setMouseY(clamp(e.clientY - containerRect.top, 0, containerRect.height));
    });
  }, [tooltipsDisabled, viewStart, viewRange]);

  const handleMouseLeave = useCallback(() => {
    if (rafId.current) { cancelAnimationFrame(rafId.current); rafId.current = null; }
    setHoverTime(null);
  }, []);

  useEffect(() => () => { if (rafId.current) cancelAnimationFrame(rafId.current); }, []);

  // ── Build the metric list shown inside the combined tooltip ───────────────
  const tooltipMetrics = useMemo(() => {
    if (tooltipsDisabled || hoverTime == null) return [];
    const list = [];

    if (sleepMode && sleepStageData?.segments?.length) {
      const seg = sleepStageData.segments.find(
        s => hoverTime >= s.startMs && hoverTime <= s.endMs
      );
      if (seg) {
        const cfg = STAGE_CFG[seg.stage];
        list.push({
          key:     "stage",
          label:   "Sleep Stage",
          color:   cfg?.color || "#6366F1",
          display: cfg?.label || seg.stage,
        });
      }
    }

    // First pass: keep each row's ORIGINAL value logic untouched
    // (forward-fill for HR/SPO2/HRV/Stress, interpolation for CGM,
    // cumulative for Steps) — plus work out whether hoverTime falls after
    // this row's very last real reading of the day.
    const rowRaws = visibleRows.map(row => {
      const cfg = METRIC_DISPLAY[row.key] || { label: row.label, unit: "" };

      if (row.key === "Steps") {
        return { row, cfg, cumulativeValue: getCumulative(row.data, hoverTime), isCumulative: true, isPastEnd: false };
      }

      const lastDataTime = getLastDataTime(row.data);
      const hasDataToday = lastDataTime != null;
      const isPastEnd = hasDataToday && hoverTime > lastDataTime;

      const normalValue = row.key === "CGM"
        ? getInterpolatedValue(row.data, hoverTime)
        : getStepValue(row.data, hoverTime);

      return { row, cfg, normalValue, isCumulative: false, isPastEnd, hasDataToday };
    });

    // Only switch into the grey "(last value)" state when EVERY row that
    // actually HAS data today is past its own last reading. Rows with no
    // data at all today (e.g. CGM with zero readings) are excluded from
    // this check — they'd otherwise stay permanently "not past end" and
    // block the fallback for every other vital forever.
    const nonCumulative = rowRaws.filter(r => !r.isCumulative);
    const rowsWithData = nonCumulative.filter(r => r.hasDataToday);
    const allPastEnd = rowsWithData.length > 0 && rowsWithData.every(r => r.isPastEnd);

    rowRaws.forEach(({ row, cfg, cumulativeValue, normalValue, isCumulative }) => {
      let display, isGrey = false;

      if (isCumulative) {
        display = cumulativeValue == null ? "--" : cumulativeValue.toLocaleString("en-IN");
      } else if (allPastEnd) {
        // getStepValue forward-fills to the last known reading regardless
        // of metric, which is exactly the "last value" we want to surface.
        const stale = getStepValue(row.data, hoverTime);
        display = stale == null ? "--" : `${stale}${cfg.unit ? " " + cfg.unit : ""} (last value)`;
        isGrey = stale != null;
      } else {
        display = normalValue == null ? "--" : `${normalValue}${cfg.unit ? " " + cfg.unit : ""}`;
      }

      list.push({ key: row.key, label: cfg.label, color: isGrey ? STALE_COLOR : row.color, display, isGrey });
    });

    return list;
  }, [visibleRows, hoverTime, tooltipsDisabled, sleepMode, sleepStageData]);

  // ── Position the tooltip to the side of the cursor, not on top of it ──────
  // Default: sits to the RIGHT of the cursor line (with a small gap) so it
  // never covers the point being hovered. If that would push it past the
  // container's right edge, it flips to the left of the cursor instead.
  const CURSOR_GAP = 18;

  const tooltipPos = useMemo(() => {
    const containerEl = containerRef.current;
    if (!containerEl || hoverTime == null) return { left: 0, top: 0 };

    const containerW = containerEl.offsetWidth;
    const containerH = containerEl.offsetHeight;
    const boxW = tooltipRef.current?.offsetWidth  || 170;
    const boxH = tooltipRef.current?.offsetHeight || 200;

    const fitsOnRight = cursorPx + CURSOR_GAP + boxW <= containerW - TOOLTIP_MARGIN;
    const rawLeft = fitsOnRight
      ? cursorPx + CURSOR_GAP
      : cursorPx - CURSOR_GAP - boxW;

    const left = clamp(rawLeft, TOOLTIP_MARGIN, containerW - boxW - TOOLTIP_MARGIN);
    const top  = clamp(mouseY - boxH / 2, TOOLTIP_MARGIN, containerH - boxH - TOOLTIP_MARGIN);

    return { left, top };
  }, [cursorPx, mouseY, hoverTime]);

  return (
    <div
      ref={containerRef}
      className="allgraph-container"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="graph-date-bar">{dateControl}</div>

      {sleepMode && (
        <div className="sleep-controls-bar">
          <VitalFilterDropdown
            rows={rows}
            visibleKeys={visibleKeys}
            onToggle={toggleKey}
          />
        </div>
      )}

      {sleepMode && (
        <SleepStageRow
          segments={sleepStageData?.segments}
          viewStart={viewStart}
          viewEnd={viewEnd}
        />
      )}

      {visibleRows.map((r) => (
        <TimelineRow
          key={r.key}
          icon={r.icon}
          label={r.label}
          data={r.data}
          color={r.color}
          viewStart={viewStart}
          viewEnd={viewEnd}
          overlay={r.overlay}
          overlayRef={r.overlayRef}
          hoverTime={tooltipsDisabled ? null : hoverTime}
        />
      ))}

      {/* Single vertical guide line, spans the whole card, follows cursor X */}
      {!tooltipsDisabled && hoverTime != null && (
        <div style={{
          position:      "absolute",
          left:          cursorPx,
          top:           60,
          bottom:        10,
          width:         "1px",
          background:    "#9ca3af",
          opacity:       0.5,
          pointerEvents: "none",
          zIndex:        5,
        }} />
      )}

      {/* Single combined tooltip — x follows hovered time, y follows the mouse */}
      {!tooltipsDisabled && (
        <CombinedTooltip
          boxRef={tooltipRef}
          hoverTime={hoverTime}
          metrics={tooltipMetrics}
          left={tooltipPos.left}
          top={tooltipPos.top}
        />
      )}
    </div>
  );
}